import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutGrid,
    FileText,
    Layers,
    LineChart,
    Files,
    Wallet,
    Settings,
    LogOut,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { UserButton, useUser } from "@/lib/clerk";
import { api_endpoints } from "@/lib/api";
import ThemeToggle from "@/components/ThemeToggle";
import SpotCarLogo from "@/components/SpotCarLogo";
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider,
} from "@/components/ui/tooltip";

export default function AppLayout() {
    const navigate = useNavigate();
    const user = useUser();
    const { t } = useTranslation();
    const [credits, setCredits] = React.useState(null);
    const [collapsed, setCollapsed] = React.useState(false);

    const refreshCredits = React.useCallback(async () => {
        try {
            const r = await api_endpoints.creditsBalance();
            setCredits(r.credits);
        } catch (e) {
            // noop
        }
    }, []);

    React.useEffect(() => {
        refreshCredits();
        const id = setInterval(refreshCredits, 30000);
        const handler = () => refreshCredits();
        window.addEventListener("credits:refresh", handler);

        const pendingReferral = localStorage.getItem("spotcar-referral");
        if (pendingReferral) {
            api_endpoints.applyReferral(pendingReferral)
                .then(() => localStorage.removeItem("spotcar-referral"))
                .catch(() => {});
        }

        return () => {
            clearInterval(id);
            window.removeEventListener("credits:refresh", handler);
        };
    }, [refreshCredits]);

    const links = [
        { to: "/app/dashboard", label: t("sidebar.dashboard"), icon: LayoutGrid },
        { to: "/app/collections", label: t("sidebar.collections"), icon: Layers },
        { to: "/app/bulk", label: t("sidebar.bulkAnalysis"), icon: Files },
        { to: "/app/compare", label: t("sidebar.compare"), icon: LineChart },
        { to: "/app/credits", label: t("sidebar.credits"), icon: Wallet },
        { to: "/app/settings", label: t("sidebar.settings"), icon: Settings },
    ];

    return (
        <TooltipProvider>
        <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-text)] font-body flex">
            {/* Sidebar */}
            <aside
                data-testid="app-sidebar"
                aria-label={t("sidebar.navigation")}
                className={`border-r-2 border-black bg-white dark:bg-zinc-950 sticky top-0 h-dvh flex flex-col transition-[width] duration-150 ${collapsed ? "w-16" : "w-60"}`}
            >
                <div className="border-b-2 border-black p-4 flex items-center gap-3">
                    <SpotCarLogo size={32} className="flex-shrink-0" />
                    {!collapsed && (
                        <div className="font-display text-lg uppercase tracking-tighter leading-none">
                            SpotCar
                        </div>
                    )}
                </div>

                <nav className="flex-1 px-2 space-y-1 mt-2 overflow-y-auto">
                    {links.map((l) => {
                        const Icon = l.icon;
                        const link = (
                            <NavLink
                                to={l.to}
                                data-testid={`sidebar-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 border-2 ${isActive ? "border-black bg-black text-white dark:border-white" : "border-transparent text-zinc-700 dark:text-zinc-300 hover:bg-[var(--surface-container-high)]"} font-mono text-[11px] uppercase tracking-[0.2em] transition-colors`
                                }
                            >
                                <Icon size={20} className="flex-shrink-0" />
                                {!collapsed && <span>{l.label}</span>}
                            </NavLink>
                        );
                        return collapsed ? (
                            <Tooltip key={l.to} delayDuration={0}>
                                <TooltipTrigger asChild>
                                    <div>{link}</div>
                                </TooltipTrigger>
                                <TooltipContent side="right">{l.label}</TooltipContent>
                            </Tooltip>
                        ) : (
                            <React.Fragment key={l.to}>{link}</React.Fragment>
                        );
                    })}
                </nav>

                <div className="border-t-2 border-black p-3 space-y-3">
                    {collapsed ? (
                        <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                                <div>
                                    <NavLink
                                        to="/app/credits"
                                        data-testid="sidebar-credits"
                                        className="block border-2 border-black bg-[#FFCC00] p-1.5 flex items-center justify-center hover:bg-black hover:text-[#FFCC00] transition-colors"
                                    >
                                        <div className="font-display text-xs leading-none">
                                            {credits ?? "—"}
                                        </div>
                                    </NavLink>
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="right">{t("sidebar.credits")}</TooltipContent>
                        </Tooltip>
                    ) : (
                        <NavLink
                            to="/app/credits"
                            data-testid="sidebar-credits"
                            className={`block border-2 border-black bg-[#FFCC00] p-2.5 hover:bg-black hover:text-[#FFCC00] transition-colors`}
                        >
                            <div className="font-mono text-[9px] uppercase tracking-[0.25em]">
                                {t("sidebar.credits")}
                            </div>
                            <div className="font-display text-2xl leading-none mt-1">
                                {credits ?? "—"}
                            </div>
                        </NavLink>
                    )}
                    <div className="flex items-center gap-3" data-sensitive>
                        <UserButton />
                        {!collapsed && user && (
                            <div className="flex-1 min-w-0">
                                <div className="font-mono text-[10px] uppercase tracking-[0.15em] truncate">
                                    {user.firstName || "User"}
                                </div>
                                <div className="font-mono text-[9px] text-zinc-500 truncate">
                                    {user.primaryEmailAddress?.emailAddress ||
                                        ""}
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")}
                        className="w-full border border-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.4)] py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] hover:bg-[var(--surface-container-high)] rounded transition-colors"
                        data-testid="sidebar-collapse"
                    >
                        {collapsed ? t("common.expand") : t("common.collapseText")}
                    </button>
                    <ThemeToggle testid="sidebar-theme-toggle" />
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0">
                <Outlet />
            </main>
        </div>
        </TooltipProvider>
    );
}
